import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Download, 
  Building2, 
  CreditCard, 
  Loader2,
  CheckCircle,
  Check,
  X,
  Clock,
  Receipt,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
  Sparkles,
  Package,
  Users,
  UserPlus,
  Crown,
  Rocket,
  Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PromoCountdown from "@/components/PromoCountdown";
import { isPromoActive, PROMO_DISCOUNT_LABEL, PROMO_DATE_LABEL } from "@/config/promo";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Billing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [payments, setPayments] = useState([]);
  const [planStatus, setPlanStatus] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [showChangePlanDialog, setShowChangePlanDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processingUpgrade, setProcessingUpgrade] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [processingCancel, setProcessingCancel] = useState(false);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [processingReactivate, setProcessingReactivate] = useState(false);
  const [billingData, setBillingData] = useState({
    company_name: "",
    cif_nif: "",
    address: "",
    city: "",
    postal_code: "",
    country: "España"
  });
  const [billingLocked, setBillingLocked] = useState(false);
  const [upgradeCalc, setUpgradeCalc] = useState(null);
  const [loadingCalc, setLoadingCalc] = useState(false);

  const openUpgradeDialog = async (planId) => {
    setSelectedPlan(planId);
    setShowChangePlanDialog(true);
    setUpgradeCalc(null);
    setLoadingCalc(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/plan/change/calculate/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpgradeCalc(res.data);
    } catch (e) {
      setUpgradeCalc(null);
    } finally {
      setLoadingCalc(false);
    }
  };

  useEffect(() => {
    if (user && user.role !== "admin" && user.role !== "super_admin") {
      toast.error("Acceso denegado: Solo administradores");
      navigate("/");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [billingRes, paymentsRes, planRes, plansRes, subRes] = await Promise.all([
        axios.get(`${API}/billing/data`, { headers }),
        axios.get(`${API}/billing/payments`, { headers }),
        axios.get(`${API}/plan/status`, { headers }),
        axios.get(`${API}/plan/available`, { headers }),
        axios.get(`${API}/subscription/status`, { headers }).catch(() => ({ data: {} }))
      ]);
      
      setBillingData(billingRes.data);
      setBillingLocked(billingRes.data.billing_locked || false);
      setPayments(paymentsRes.data?.payments || paymentsRes.data || []);
      // Merge: subscription data first, then plan status overrides (plan_type from DB is authoritative)
      setPlanStatus(prev => {
        // Smart merge: subscription/status (subRes) has live Stripe data (dates, cancel flags)
        // plan/status (planRes) has plan limits, trial info — authoritative for plan_type/limits
        // Rule: never overwrite a non-empty value with an empty string
        const merged = { ...subRes.data, ...planRes.data };
        const subData = subRes.data || {};
        // Preserve Stripe live fields if planRes returned empty strings
        if (!merged.current_period_end && subData.current_period_end)
          merged.current_period_end = subData.current_period_end;
        if (!merged.cancel_at_period_end && subData.cancel_at_period_end)
          merged.cancel_at_period_end = subData.cancel_at_period_end;
        if (!merged.has_subscription && subData.has_subscription)
          merged.has_subscription = subData.has_subscription;
        return merged;
      });
      setAvailablePlans(plansRes.data.plans || []);
    } catch (error) {
      console.error("Error fetching billing data:", error);
      toast.error("Error al cargar datos de facturación");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!selectedPlan) return;
    
    setProcessingUpgrade(true);
    try {
      const token = localStorage.getItem("token");
      
      // Always use /plan/upgrade — backend handles all cases:
      // trial → new subscription checkout, upgrade → subscription modify, downgrade → direct switch
      const res = await axios.post(`${API}/plan/upgrade`, {
        new_plan_type: selectedPlan,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.requires_payment && res.data.hosted_invoice_url) {
        // Proration invoice — redirect to Stripe hosted invoice page
        window.location.href = res.data.hosted_invoice_url;
      } else if (res.data.requires_payment && res.data.checkout_url) {
        // New subscription — redirect to Stripe Checkout
        window.location.href = res.data.checkout_url;
      } else {
        // Payment auto-charged or no payment needed
        toast.success(res.data.message || "Plan actualizado");
        setShowChangePlanDialog(false);
        fetchData();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.blockers) {
        toast.error(detail.blockers.map(b => b.message).join('. '));
      } else {
        toast.error(typeof detail === 'string' ? detail : "Error al procesar el cambio de plan");
      }
    } finally {
      setProcessingUpgrade(false);
    }
  };

  const handleDownloadInvoice = async (paymentId, invoiceNumber, stripeInvoiceId) => {
    setDownloading(paymentId);
    try {
      const token = localStorage.getItem("token");

      // ── If there's a Stripe invoice ID, always fetch a FRESH URL from Stripe ──
      if (stripeInvoiceId && stripeInvoiceId.startsWith("in_")) {
        const res = await axios.get(
          `${API}/billing/invoice/${paymentId}/stripe-url`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const { url, type, message } = res.data;

        if (url) {
          // Open Stripe's own hosted invoice page in a new tab
          window.open(url, "_blank", "noopener,noreferrer");
          toast.success("Abriendo factura oficial de Stripe...");
          return;
        }

        // Stripe returned no URL yet
        toast.info(message || "Estamos recuperando su factura de la plataforma de pagos, por favor reintente en unos segundos.");
        return;
      }

      // ── Fallback: no Stripe invoice → download local PDF ──
      const response = await fetch(
        `${API}/billing/invoice/${paymentId}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("Error al descargar");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${invoiceNumber || "factura"}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Factura descargada");
    } catch (error) {
      toast.error("Error al obtener la factura. Por favor, reintente.");
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("es-ES", {
      year: "numeric", month: "long", day: "numeric"
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(amount);
  };

  const getPlanOrder = (planType) => {
    const order = { trial: 0, basic: 1, pro: 2, enterprise: 3 };
    return order[planType] || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const currentPlanOrder = getPlanOrder(planStatus?.plan_type);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Facturación</h1>
        <p className="text-slate-500 mt-1">Gestiona tu plan, datos fiscales y facturas</p>
      </div>

      {/* Plans Section - Gradient Background */}
      <div className="rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #ec4899 100%)' }}>
        <div className="px-6 pt-8 pb-4">
          {/* Current Plan Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white">Tu Plan Actual</h2>
            <p className="text-white/70 mt-1">
              {planStatus?.is_trial 
                ? `Trial - ${planStatus.trial_days_remaining > 0 
                    ? `${planStatus.trial_days_remaining} días restantes` 
                    : `${planStatus.trial_hours_remaining || 0} horas restantes`}`
                : planStatus?.plan_name}
            </p>
          </div>

          {/* Usage Stats */}
          <div className="mb-8 p-5 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
            <h3 className="font-bold text-white mb-4 text-sm text-center uppercase tracking-wider">Tu uso actual:</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-3xl font-black text-white">{planStatus?.current_items?.toLocaleString() || 0}</p>
                <p className="text-sm text-white/70">
                  de {planStatus?.max_items >= 999999 ? '∞' : planStatus?.max_items?.toLocaleString()} artículos
                </p>
              </div>
              <div>
                <p className="text-3xl font-black text-white">{planStatus?.current_customers?.toLocaleString() || 0}</p>
                <p className="text-sm text-white/70">
                  de {planStatus?.max_customers >= 999999 ? '∞' : planStatus?.max_customers?.toLocaleString()} clientes
                </p>
              </div>
              <div>
                <p className="text-3xl font-black text-white">{planStatus?.current_users || 1}</p>
                <p className="text-sm text-white/70">
                  de {planStatus?.max_users >= 999 ? '∞' : planStatus?.max_users} usuarios
                </p>
              </div>
            </div>
            
            {/* Subscription info */}
            {planStatus?.has_subscription && (
              <div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-between gap-4">
                <div className="text-sm text-white/80 flex items-center gap-2 flex-wrap">
                  <span className="font-medium">Suscripción anual</span>
                  {planStatus.cancel_at_period_end ? (
                    <>
                      <Badge className="bg-amber-500/20 text-amber-200 border border-amber-400/30">Baja programada</Badge>
                      {planStatus.current_period_end && (
                        <span className="text-amber-200 font-semibold">
                          · Acceso hasta el {new Date(planStatus.current_period_end).toLocaleDateString('es-ES', {day:'2-digit', month:'2-digit', year:'numeric'})}
                        </span>
                      )}
                    </>
                  ) : (
                    planStatus.current_period_end && (
                      <span className="text-white/60">
                        · Próxima renovación: <span className="text-white/90 font-medium">{new Date(planStatus.current_period_end).toLocaleDateString('es-ES', {day:'2-digit', month:'2-digit', year:'numeric'})}</span>
                      </span>
                    )
                  )}
                </div>
                {!planStatus.cancel_at_period_end && (
                  <span className="text-xs text-white/50 flex-shrink-0">Puedes gestionar la baja más abajo</span>
                )}
              </div>
            )}
          </div>

          {/* Plan Cards */}
          <div className="pb-8">
            <h3 className="text-lg font-bold text-white mb-1">Cambiar de Plan</h3>
            <p className="text-white/60 text-sm mb-6">Selecciona el plan que mejor se adapte a tus necesidades</p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {availablePlans.map((plan) => {
                const planOrder = getPlanOrder(plan.id);
                const isCurrentPlan = planStatus?.plan_type === plan.id;
                const isUpgrade = planOrder > currentPlanOrder;
                const isPro = plan.id === "pro";

                const getPlanIcon = (planId) => {
                  switch (planId) {
                    case "basic": return <Rocket className="h-6 w-6 text-white" />;
                    case "pro": return <Crown className="h-6 w-6 text-white" />;
                    case "enterprise": return <Building2 className="h-6 w-6 text-white" />;
                    default: return <Rocket className="h-6 w-6 text-white" />;
                  }
                };

                return (
                  <div key={plan.id} className="relative" data-testid={`plan-card-${plan.id}`}>
                    {/* Badges row - flex to prevent overlap */}
                    <div className="absolute -top-3 left-4 right-4 z-10 flex justify-between items-center">
                      {/* Left badge */}
                      <div>
                        {plan.recommended && !isCurrentPlan && (
                          <Badge className="bg-white text-black font-bold px-4 py-1" data-testid={`plan-popular-badge-${plan.id}`}>POPULAR</Badge>
                        )}
                      </div>
                      {/* Right badge */}
                      <div>
                        {isCurrentPlan && (
                          <Badge className="bg-emerald-500 text-white font-bold px-4 py-1" data-testid={`plan-current-badge-${plan.id}`}>Plan Actual</Badge>
                        )}
                        {!isCurrentPlan && isPromoActive() && (
                          <div className="flex flex-col items-end gap-1">
                            <Badge className="bg-red-500 text-white font-black px-3 py-1 shadow-lg text-xs" data-testid={`billing-discount-badge-${plan.id}`}>
                              {PROMO_DISCOUNT_LABEL} hasta {PROMO_DATE_LABEL}
                            </Badge>
                            <PromoCountdown className="text-[10px] font-bold text-red-600" data-testid={`billing-countdown-${plan.id}`} />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={`relative rounded-2xl overflow-hidden transition-all duration-300 h-full ${
                      isPro ? 'transform scale-105' : ''
                    }`}>
                      <div className={`absolute inset-0 ${
                        isPro ? 'bg-gradient-to-br from-black via-slate-900 to-black' : 'bg-white'
                      }`}></div>
                      
                      <div className={`relative p-6 h-full flex flex-col ${
                        isPro ? 'border-4 border-purple-500/50 rounded-2xl' : 'border-2 border-slate-200 rounded-2xl'
                      }`}>
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-5">
                          <div className={`h-10 w-10 rounded-xl flex items-center justify-center shadow-lg ${
                            isPro ? 'bg-gradient-to-br from-purple-500 to-pink-500' : 'bg-black'
                          }`}>
                            {getPlanIcon(plan.id)}
                          </div>
                          <div>
                            <h3 className={`text-lg font-bold ${isPro ? 'text-white' : 'text-black'}`}>
                              {plan.name}
                            </h3>
                            <p className={`text-xs ${isPro ? 'text-slate-600' : 'text-slate-500'}`}>
                              {isPro ? 'Más vendido' : 'Para empezar'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Price */}
                        <div className="mb-6">
                          <span className={`text-4xl font-black ${isPro ? 'text-white' : 'text-black'}`}>
                            {plan.price} €
                          </span>
                          <p className={`text-sm mt-1 font-medium ${isPro ? 'text-slate-600' : 'text-slate-600'}`}>
                            + IVA (21%) = <strong>{(plan.price * 1.21).toFixed(2)} €</strong>/año
                          </p>
                        </div>

                        {/* Action Button */}
                        <div className="mb-6">
                          {isCurrentPlan ? (
                            <Button disabled className={`w-full font-bold rounded-full py-5 ${
                              isPro ? 'bg-white/20 text-white/60' : 'bg-slate-100 text-slate-600'
                            }`} data-testid={`plan-btn-current-${plan.id}`}>
                              PLAN ACTUAL
                            </Button>
                          ) : isUpgrade ? (
                            <Button 
                              className={`w-full font-bold rounded-full py-5 ${
                                isPro 
                                  ? 'bg-white text-black hover:bg-slate-100' 
                                  : 'bg-black text-white hover:bg-black/90'
                              }`}
                              onClick={() => openUpgradeDialog(plan.id)}
                              data-testid={`plan-btn-upgrade-${plan.id}`}
                            >
                              UPGRADE
                            </Button>
                          ) : (
                            <Button 
                              variant="outline"
                              disabled
                              className={`w-full font-bold rounded-full py-5 opacity-50 ${
                                isPro ? 'border-white/30 text-white/50' : ''
                              }`}
                            >
                              PLAN INFERIOR
                            </Button>
                          )}
                        </div>

                        {/* Features */}
                        <div className="space-y-3 flex-grow">
                          {plan.features?.map((feature, idx) => (
                            <div key={idx} className="flex items-start gap-3 text-sm">
                              <Check className={`h-4 w-4 flex-shrink-0 mt-0.5 ${isPro ? 'text-white' : 'text-black'}`} />
                              <span className={`font-medium ${isPro ? 'text-white' : 'text-slate-700'}`}>
                                {feature}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Billing Data */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              {billingLocked ? <Lock className="h-5 w-5 text-blue-600" /> : <Building2 className="h-5 w-5 text-blue-600" />}
            </div>
            <div>
              <CardTitle>Datos Fiscales</CardTitle>
              <CardDescription>
                {billingLocked ? "Datos verificados y bloqueados a través de Stripe" : "Se recogerán automáticamente durante el proceso de pago en Stripe"}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {billingLocked ? (
            /* ── BLOQUEADO (post-pago): mostrar datos en modo lectura ── */
            <>
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-testid="billing-locked-message">
                <Lock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  <strong>Por seguridad fiscal, estos datos no se pueden modificar en SkiFlow.</strong> Fueron registrados y validados por Stripe durante el proceso de pago. Si necesitas actualizar tu razón social o CIF/NIF, contacta con <strong>Soporte</strong>.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500 uppercase tracking-wide">Razón Social</Label>
                  <p className="font-medium text-slate-800 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200" data-testid="billing-company-name">{billingData.company_name || "—"}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500 uppercase tracking-wide">NIF/CIF</Label>
                  <p className="font-medium text-slate-800 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200" data-testid="billing-cif-nif">{billingData.cif_nif || "—"}</p>
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs text-slate-500 uppercase tracking-wide">Dirección Fiscal</Label>
                  <p className="font-medium text-slate-800 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200" data-testid="billing-address">{billingData.address || "—"}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500 uppercase tracking-wide">Ciudad</Label>
                  <p className="font-medium text-slate-800 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200" data-testid="billing-city">{billingData.city || "—"}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500 uppercase tracking-wide">Código Postal</Label>
                  <p className="font-medium text-slate-800 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200" data-testid="billing-postal-code">{billingData.postal_code || "—"}</p>
                </div>
              </div>
            </>
          ) : (
            /* ── SIN PLAN DE PAGO: información de que Stripe los recogerá ── */
            <div className="flex flex-col items-center justify-center py-8 text-center space-y-4" data-testid="billing-no-data-message">
              <div className="h-16 w-16 rounded-full bg-blue-50 flex items-center justify-center">
                <CreditCard className="h-8 w-8 text-blue-400" />
              </div>
              <div>
                <p className="font-semibold text-slate-700 text-base">Los datos fiscales se registran a través de Stripe</p>
                <p className="text-sm text-slate-500 mt-1 max-w-md">
                  Al contratar un plan, Stripe te pedirá tu <strong>CIF/NIF</strong> y <strong>dirección fiscal</strong> antes de procesar el pago. Esto garantiza que todas tus facturas sean legalmente válidas.
                </p>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg w-full max-w-md">
                <p className="text-xs text-blue-700 flex items-center gap-2">
                  <Lock className="h-3 w-3 flex-shrink-0" />
                  Una vez completado el pago, los datos quedarán verificados y bloqueados para garantizar la integridad de las facturas.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Receipt className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <CardTitle>Historial de Pagos</CardTitle>
              <CardDescription>Registro de todos los pagos realizados</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CreditCard className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p className="font-medium">No hay pagos registrados</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Fecha</TableHead>
                  <TableHead>Nº Factura Stripe</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead className="text-right">Importe</TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                  <TableHead className="text-right">Factura</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{formatDate(payment.date)}</TableCell>
                    <TableCell>
                      <div className="font-mono text-sm">{payment.invoice_number || "—"}</div>
                      {payment.stripe_payment_intent && (
                        <div className="text-xs text-slate-600 mt-0.5" title="Stripe Payment Intent ID">
                          PI: {payment.stripe_payment_intent}
                        </div>
                      )}
                    </TableCell>
                    <TableCell><Badge variant="outline">{payment.plan_name || payment.plan}</Badge></TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(payment.amount)}</TableCell>
                    <TableCell className="text-center">
                      {payment.status === "paid" ? (
                        <Badge className="bg-emerald-500/20 text-emerald-700"><CheckCircle className="h-3 w-3 mr-1" />Pagado</Badge>
                      ) : (
                        <Badge className="bg-amber-500/20 text-amber-700"><Clock className="h-3 w-3 mr-1" />Pendiente</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleDownloadInvoice(payment.id, payment.invoice_number, payment.stripe_invoice_id)} disabled={downloading === payment.id}>
                        {downloading === payment.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1" />{payment.stripe_invoice_id ? "Stripe PDF" : "PDF"}</>}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Change Plan Dialog */}
      <Dialog open={showChangePlanDialog} onOpenChange={setShowChangePlanDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle>Upgrade de Plan</DialogTitle>
            <DialogDescription>
              {planStatus?.has_subscription 
                ? "Se te cobrará la diferencia proporcional por los días restantes de tu suscripción."
                : "Serás redirigido a Stripe para completar el pago."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Plan change summary */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="text-center">
                <p className="text-sm text-slate-500">Plan Actual</p>
                <p className="font-bold capitalize">{planStatus?.plan_type || "trial"}</p>
              </div>
              <ArrowUpRight className="h-6 w-6 text-emerald-600" />
              <div className="text-center">
                <p className="text-sm text-slate-500">Nuevo Plan</p>
                <p className="font-bold text-emerald-600 capitalize">{selectedPlan}</p>
              </div>
            </div>

            {/* Prorated calculation details */}
            {loadingCalc ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                <span className="ml-2 text-sm text-slate-500">Calculando prorrateo...</span>
              </div>
            ) : upgradeCalc ? (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-3" data-testid="upgrade-calc-details">
                {upgradeCalc.current_plan !== "trial" && upgradeCalc.days_remaining > 0 && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Días restantes de tu período</span>
                      <span className="font-bold">{upgradeCalc.days_remaining} días</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Crédito plan actual ({upgradeCalc.current_plan_name})</span>
                      <span className="font-bold text-green-700">-{upgradeCalc.credit_amount?.toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Coste proporcional {upgradeCalc.new_plan_name}</span>
                      <span className="font-bold">{upgradeCalc.prorate_amount?.toFixed(2)} €</span>
                    </div>
                    <hr className="border-blue-200" />
                  </>
                )}
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-blue-900">
                    {upgradeCalc.current_plan === "trial" ? "Total anual" : "Diferencia a pagar"}
                  </span>
                  <div className="text-right">
                    <span className="text-2xl font-black text-blue-900">{upgradeCalc.amount_to_pay?.toFixed(2)} €</span>
                    <p className="text-xs text-blue-600">+ IVA (21%) = {(upgradeCalc.amount_to_pay * 1.21).toFixed(2)} €</p>
                  </div>
                </div>
                {upgradeCalc.blockers?.length > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg mt-2">
                    {upgradeCalc.blockers.map((b, i) => (
                      <p key={i} className="text-sm text-red-700">{b.message}</p>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 bg-blue-500/10 rounded-lg">
                <p className="font-medium text-blue-800">Serás redirigido a Stripe para completar el pago</p>
                <p className="text-sm text-blue-600 mt-1">
                  El IVA (21%) se añadirá al total.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangePlanDialog(false)} data-testid="upgrade-cancel-btn">Cancelar</Button>
            <Button 
              onClick={handleConfirmPlanChange} 
              disabled={processingUpgrade || loadingCalc || (upgradeCalc?.blockers?.length > 0)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="upgrade-confirm-btn"
            >
              {processingUpgrade ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {upgradeCalc?.amount_to_pay > 0 ? `Pagar ${(upgradeCalc.amount_to_pay * 1.21).toFixed(2)} €` : "Ir a Pagar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== DAR DE BAJA ==================== */}
      {planStatus?.plan_type && (
        <Card className={planStatus.is_trial ? "border-orange-200 bg-orange-50/30" : "border-red-200 bg-red-50/30"}>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${planStatus.is_trial ? "bg-orange-500/20" : "bg-red-500/20"}`}>
                <AlertTriangle className={`h-5 w-5 ${planStatus.is_trial ? "text-orange-600" : "text-red-600"}`} />
              </div>
              <div>
                <CardTitle className={planStatus.is_trial ? "text-orange-900" : "text-red-900"}>
                  {planStatus.is_trial ? "Cancelar Trial" : "Dar de Baja"}
                </CardTitle>
                <CardDescription className={planStatus.is_trial ? "text-orange-600/80" : "text-red-600/80"}>
                  {planStatus.is_trial ? "Desactivar tu cuenta de prueba" : "Cancelar tu suscripción anual"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
          {planStatus.is_trial ? (
              /* ── TRIAL: acceso se pierde inmediatamente ── */
              <div className="space-y-4">
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-orange-800">
                    Si cancelas el trial, <strong>perderás el acceso a todos tus datos de forma inmediata</strong>.
                    No se realizará ningún cargo. No es necesario introducir datos de pago.
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="border-orange-300 text-orange-700 hover:bg-orange-100 hover:text-orange-800 font-semibold"
                  onClick={() => setShowCancelDialog(true)}
                  data-testid="cancelar-trial-btn"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Cancelar Trial y perder acceso
                </Button>
              </div>
            ) : planStatus.cancel_at_period_end ? (
              /* ── ESTADO: CANCELACIÓN PENDIENTE → mostrar "Reactivar" ── */
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="cancel-pending-info">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-900">Baja programada</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Tu suscripción se cancelará automáticamente el{" "}
                        <strong>{planStatus.current_period_end ? new Date(planStatus.current_period_end).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" }) : "-"}</strong>.
                        Hasta esa fecha, seguirás teniendo acceso completo a tu plan <strong className="capitalize">{planStatus.plan_type}</strong>.
                      </p>
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 font-semibold"
                  onClick={() => setShowReactivateDialog(true)}
                  data-testid="reactivar-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Reactivar Suscripción
                </Button>
              </div>
            ) : (
              /* ── ESTADO NORMAL: mostrar botón "Cancelar" ── */
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  {planStatus.has_subscription ? (
                    <>
                      Si cancelas tu suscripción, tu plan <strong className="capitalize">{planStatus.plan_type}</strong> seguirá activo hasta el final del periodo contratado
                      {planStatus.current_period_end && (
                        <> (<strong>{new Date(planStatus.current_period_end).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })}</strong>)</>
                      )}.
                      Después de esa fecha, tu cuenta pasará a modo Trial con funcionalidades limitadas.
                    </>
                  ) : (
                    <>
                      Al dar de baja, tu plan <strong className="capitalize">{planStatus.plan_type}</strong> se desactivará y tu cuenta pasará a modo Trial con funcionalidades limitadas.
                    </>
                  )}
                </p>
                <Button
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800 font-semibold"
                  onClick={() => setShowCancelDialog(true)}
                  data-testid="dar-de-baja-btn"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Cancelar Suscripción
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cancel Subscription / Trial Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-md" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${planStatus?.is_trial ? "text-orange-900" : "text-red-900"}`}>
              <AlertTriangle className={`h-5 w-5 ${planStatus?.is_trial ? "text-orange-600" : "text-red-600"}`} />
              {planStatus?.is_trial ? "Cancelar Trial" : "Cancelar Suscripción"}
            </DialogTitle>
            <DialogDescription>
              {planStatus?.is_trial
                ? "Esta acción es inmediata e irreversible."
                : "La suscripción no se cancelará inmediatamente."
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {planStatus?.is_trial ? (
              /* ── TRIAL dialog content ── */
              <>
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl space-y-2">
                  <p className="font-semibold text-orange-900 text-sm">¿Estás seguro? Esto NO se puede deshacer.</p>
                  <ul className="text-sm text-orange-800 space-y-1 list-disc list-inside">
                    <li>Perderás el acceso <strong>inmediatamente</strong></li>
                    <li>Todos tus datos (artículos, clientes, alquileres) quedarán inaccesibles</li>
                    <li>No se realizará ningún cobro</li>
                    <li>No recibirás más correos de recordatorio</li>
                  </ul>
                </div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <p className="text-xs text-slate-500">Si cambias de opinión más adelante, contacta con soporte para recuperar tu cuenta.</p>
                </div>
              </>
            ) : (
              /* ── PAID PLAN dialog content ── */
              <>
                <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Plan actual</span>
                    <span className="font-bold capitalize">{planStatus?.plan_type}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Acceso hasta</span>
                    <span className="font-bold">
                      {planStatus?.current_period_end
                        ? new Date(planStatus.current_period_end).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })
                        : "Fin del periodo actual"
                      }
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Después de la baja</span>
                    <span className="font-bold text-amber-700">Modo Trial (limitado)</span>
                  </div>
                </div>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-sm text-amber-800">
                    <strong>¿Estás seguro?</strong> Tu suscripción <strong className="capitalize">{planStatus?.plan_type}</strong> permanecerá activa hasta el{" "}
                    <strong>{planStatus?.current_period_end ? new Date(planStatus.current_period_end).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" }) : "fin del periodo"}</strong>.
                    {" "}No se realizará ningún reembolso. Podrás reactivarla antes de esa fecha.
                  </p>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)} data-testid="cancel-dialog-back">
              Volver
            </Button>
            <Button
              onClick={async () => {
                setProcessingCancel(true);
                try {
                  const token = localStorage.getItem("token");
                  if (planStatus?.is_trial) {
                    // Trial: cancel immediately, no Stripe
                    await axios.post(`${API}/subscription/cancel-trial`, {}, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success("Trial cancelado. Tu acceso ha sido desactivado.");
                    setShowCancelDialog(false);
                    // Log out since account is now inactive
                    setTimeout(() => {
                      localStorage.removeItem("token");
                      window.location.href = "/login";
                    }, 2000);
                  } else {
                    // Paid plan: always use /subscription/cancel (handles both Stripe and manual)
                    const res = await axios.post(`${API}/subscription/cancel`, {}, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    const friendlyMsg = res.data?.message || "Suscripción cancelada. Activa hasta fin del periodo.";
                    toast.success(friendlyMsg);
                    setShowCancelDialog(false);
                    // Immediate optimistic UI update with the real period_end from backend
                    const periodEnd = res.data?.current_period_end || planStatus?.current_period_end || "";
                    setPlanStatus(prev => prev ? {
                      ...prev,
                      cancel_at_period_end: true,
                      current_period_end: periodEnd,
                      subscription_ends_at: periodEnd,
                    } : prev);
                    // Background refresh for full sync
                    fetchData();
                  }
                } catch (e) {
                  const errDetail = e.response?.data?.detail;
                  const errMsg = typeof errDetail === "string"
                    ? errDetail
                    : "Error al cancelar la suscripción. Por favor contacta con soporte.";
                  toast.error(errMsg, { duration: 8000 });
                } finally {
                  setProcessingCancel(false);
                }
              }}
              disabled={processingCancel}
              className={planStatus?.is_trial ? "bg-orange-600 hover:bg-orange-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
              data-testid="cancel-dialog-confirm"
            >
              {processingCancel ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {planStatus?.is_trial ? "Sí, cancelar trial y perder acceso" : "Sí, cancelar suscripción"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivate Subscription Confirmation Dialog */}
      <Dialog open={showReactivateDialog} onOpenChange={setShowReactivateDialog}>
        <DialogContent className="sm:max-w-md" data-testid="reactivate-dialog">
          <DialogHeader>
            <DialogTitle className="text-emerald-900 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
              Reactivar Suscripción
            </DialogTitle>
            <DialogDescription>
              Tu suscripción volverá a renovarse automáticamente.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <p className="text-sm text-emerald-800">
                Al reactivar, tu suscripción <strong className="capitalize">{planStatus?.plan_type}</strong> se renovará automáticamente al finalizar el periodo actual
                {planStatus?.current_period_end && (
                  <> el <strong>{new Date(planStatus.current_period_end).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })}</strong></>
                )}. Sin interrupciones en el servicio.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReactivateDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                setProcessingReactivate(true);
                try {
                  const token = localStorage.getItem("token");
                  await axios.post(`${API}/subscription/reactivate`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  toast.success("✅ Suscripción reactivada. Se renovará automáticamente.");
                  setShowReactivateDialog(false);
                  fetchData();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Error al reactivar la suscripción");
                } finally {
                  setProcessingReactivate(false);
                }
              }}
              disabled={processingReactivate}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="reactivate-confirm-btn"
            >
              {processingReactivate ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Sí, reactivar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
